import type { ActionFormRow, MinutePerson } from '../actions/types'
import type { MinuteItemKind, MinuteItemRow } from '../minuteItems/types'
import type { ExcelImportedRow } from './types'

/**
 * Excel ön izleme satırlarını ekranın **editör satırlarına** çevirir ve aktarımda replace mantığını
 * uygular (088, US1/US2). Saf fonksiyonlar — durum yönetimi çağırana ait, doğrudan test edilebilir.
 *
 * <p>Aktarılan satırlar `source: 'excel'` işaretlenir. İşaret **yalnız istemcidedir**: `POST /minutes`
 * gövdesini kuran `buildActionPayload`/`buildMinuteItemsPayload` bu alanı taşımaz, yani sunucu sözleşmesi
 * değişmez (FR-021).
 */

/**
 * Sunucudan gelen kişi anlık görüntüsü ekranın kişi yapısıyla birebir aynıdır; **rolüyle** (Ana 340 / Yan
 * 341) kopyalanarak taşınır. Rol şart: yeni aksiyonda tam bir Ana Sorumlu yoksa Yayınla 400 verir.
 * Prensip sayfasının Liste kolonundan gelen kişiler **liste etiketini** de taşır; tablo onları liste adı
 * altında gruplar (115) — etiket burada düşerse gruplama da kayıttaki liste bağı da kaybolur.
 */
function toPerson(p: MinutePerson): MinutePerson {
  return { ...p }
}

/**
 * Excel sayfa türü → ekrandaki madde editörü. Aksiyon sayfası burada YOKTUR: onun satırları ayrı bir
 * modele (`ActionFormRow`) dönüşür, bu yüzden çağıran taraf `ACTION`'ı önce ayırır.
 */
export const ITEM_KIND_BY_SHEET: Record<'AGENDA' | 'PRINCIPLE', MinuteItemKind> = {
  AGENDA: 'agenda',
  PRINCIPLE: 'principles',
}

/** Aksiyon satırları: Sıra No sırası korunur; numara/durum alanları sunucuya bırakılır. */
export function excelRowsToActionRows(rows: ExcelImportedRow[]): ActionFormRow[] {
  return rows.map((row) => ({
    key: crypto.randomUUID(),
    id: null,
    recordNumber: '',
    name: row.name,
    description: row.description ?? '',
    urgencyLevelId: row.urgencyLevelId,
    importanceLevelId: row.importanceLevelId,
    deadline: row.deadline ?? '',
    statusId: null,
    assignees: row.assignees.map(toPerson),
    source: 'excel',
  }))
}

/** Gündem/Prensip satırları. Açıklama yalnız Gündem'de anlamlıdır; Prensip'te boş kalır. */
export function excelRowsToItemRows(
  rows: ExcelImportedRow[],
  kind: MinuteItemKind,
): MinuteItemRow[] {
  return rows.map((row) => ({
    key: crypto.randomUUID(),
    id: null,
    recordNumber: '',
    name: row.name,
    description: kind === 'agenda' ? (row.description ?? '') : '',
    responsibles: row.assignees.map(toPerson),
    source: 'excel',
  }))
}

/**
 * İkinci (ve sonraki) aktarımda **Excel kaynaklı** satırların yerine geçer; elle girilmiş satırlar
 * korunur ve listedeki sıraları bozulmaz — yeni Excel satırları sona eklenir (FR-022).
 *
 * <p>İçerik imzasıyla eşleştirme bilinçle KULLANILMAZ: kullanıcı aktardığı satırı elle düzenlediğinde
 * imza değişir ve satır "yeni" sayılıp sessiz mükerrer üretirdi.
 */
export function replaceExcelRows<T extends { source?: 'manual' | 'excel' }>(
  current: T[],
  incoming: T[],
): T[] {
  return [...current.filter((row) => row.source !== 'excel'), ...incoming]
}
